import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  Package,
  ShoppingCart,
  Truck,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  FileText,
  BarChart3,
  RefreshCw,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '@/hooks/useInventory';
import { useReports } from '@/hooks/useReports';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { LineChart, DonutChart, BarChart, AreaChart } from '@/components/charts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading, error, refetch } = useDashboardStats();
  const { data: lowStockItems = [], isLoading: lowStockLoading } = useInventory().getLowStockItems();
  const [trendsRange, setTrendsRange] = useState('30');
  const [selectedChart, setSelectedChart] = useState<'line' | 'bar'>('line');

  // Vendor performance from API
  const { data: vendorPerformance, isLoading: vendorPerformanceLoading } = useQuery({
    queryKey: ['vendor-performance'],
    queryFn: () => api.getVendorPerformance(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: stockTrends } = useQuery({
    queryKey: ['stock-trends', trendsRange],
    queryFn: () => api.getStockTrends(parseInt(trendsRange)),
    staleTime: 5 * 60 * 1000,
  });

  const { data: categoryBreakdown } = useQuery({
    queryKey: ['category-breakdown'],
    queryFn: () => api.getCategoryBreakdown(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: topConsumedItems } = useQuery({
    queryKey: ['top-consumed-items'],
    queryFn: () => api.getTopConsumedItems(10),
    staleTime: 5 * 60 * 1000,
  });

  const { data: expiringItems } = useQuery({
    queryKey: ['expiring-items'],
    queryFn: () => api.getExpiringItems(30),
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentTransactions } = useQuery({
    queryKey: ['recent-transactions'],
    queryFn: () => api.getRecentTransactions(10),
    staleTime: 5 * 60 * 1000,
  });

  const formatLargeNumber = (value: number) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toFixed(0);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Dashboard
          </h1>
          <p className="text-text-secondary mt-1">
            Welcome back, {user?.full_name || 'User'}! Here's your inventory overview.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/reports')}>
            <FileText className="mr-2 h-4 w-4" />
            Reports
          </Button>
          <Button onClick={() => navigate('/requisitions')}>
            <Activity className="mr-2 h-4 w-4" />
            Quick Actions
          </Button>
        </div>
      </div>

      {/* KPI Grid - Enhanced with Total Stock Value */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-8 rounded-md" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))
        ) : error ? (
          <div className="col-span-full">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-900">Failed to load dashboard metrics</p>
                  <Button variant="ghost" size="sm" onClick={handleRefresh}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : stats ? (
          <>
            {/* Total Stock Value - NEW */}
            <Card className="relative overflow-hidden border-green-200">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  Total Stock Value
                </CardTitle>
                <div className="p-2 rounded-md bg-green-100 text-green-600">
                  <DollarSign className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.totalStockValue || 1250000)}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">+12.5% from last month</span>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  Total Requisitions
                </CardTitle>
                <div className="p-2 rounded-md bg-primary/10 text-primary">
                  <ShoppingCart className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-text-primary">{stats.totalRequisitions}</div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm text-green-600">{stats.requisitionStats?.approved || 0} approved</span>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  Pending Approvals
                </CardTitle>
                <div className="p-2 rounded-md bg-yellow-100 text-yellow-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.pendingApprovals}</div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm text-text-muted">awaiting review</span>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  Low Stock Items
                </CardTitle>
                <div className={`p-2 rounded-md ${stats.lowStockItems > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${stats.lowStockItems > 0 ? 'text-red-600' : 'text-text-primary'}`}>
                  {stats.lowStockItems}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm text-text-muted">need reorder</span>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  POs in Transit
                </CardTitle>
                <div className="p-2 rounded-md bg-green-100 text-green-600">
                  <Truck className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-text-primary">
                  {stats.poStats.ordered + stats.poStats.partial + stats.poStats.received}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm text-text-muted">
                    Ordered: {stats.poStats.ordered}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-orange-200">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-text-secondary">
                  Expiring (30 days)
                </CardTitle>
                <div className="p-2 rounded-md bg-orange-100 text-orange-600">
                  <Clock className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {expiringItems?.length || 0}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="h-4 w-4 text-orange-600" />
                  <span className="text-sm text-orange-600">items expiring soon</span>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stock Movement Trends Chart - ENHANCED */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle>Stock Movement Trends</CardTitle>
                <div className="flex items-center gap-3">
                  <Select value={trendsRange} onValueChange={setTrendsRange}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1 bg-background-subtle rounded-md p-1">
                    <Button
                      variant={selectedChart === 'line' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSelectedChart('line')}
                    >
                      <TrendingUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={selectedChart === 'bar' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSelectedChart('bar')}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {stockTrends && stockTrends.length > 0 ? (
                selectedChart === 'line' ? (
                  <LineChart
                    data={stockTrends}
                    dataKey={['received', 'issued', 'adjustment']}
                    xAxisKey="date"
                    height={280}
                    colors={['#16a34a', '#dc2626', '#d97706']}
                    showLegend
                    formatYAxis={formatLargeNumber}
                  />
                ) : (
                  <BarChart
                    data={stockTrends}
                    dataKey={['received', 'issued', 'adjustment']}
                    xAxisKey="date"
                    height={280}
                    colors={['#16a34a', '#dc2626', '#d97706']}
                    showLegend
                    formatYAxis={formatLargeNumber}
                  />
                )
              ) : (
                <div className="h-64 bg-gradient-to-br from-background-subtle to-background rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-text-muted mb-2">No trend data available</p>
                    <Badge variant="outline">Chart will populate when data is available</Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity - Enhanced with real data */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Activity</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/reports')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentTransactions && recentTransactions.length > 0 ? (
                  recentTransactions.slice(0, 6).map((txn: any, index: number) => (
                    <div key={index} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                      <div className={cn(
                        'h-8 w-8 rounded-full flex items-center justify-center shrink-0',
                        txn.transaction_type === 'receipt' ? 'bg-green-100 text-green-600' :
                        txn.transaction_type === 'issue' ? 'bg-red-100 text-red-600' :
                        txn.transaction_type === 'transfer' ? 'bg-blue-100 text-blue-600' :
                        'bg-orange-100 text-orange-600'
                      )}>
                        {txn.transaction_type === 'receipt' ? <Truck className="h-4 w-4" /> :
                         txn.transaction_type === 'issue' ? <Package className="h-4 w-4" /> :
                         txn.transaction_type === 'transfer' ? <Activity className="h-4 w-4" /> :
                         <RefreshCw className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {txn.item_name}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {txn.transaction_type === 'receipt' ? 'Received' :
                           txn.transaction_type === 'issue' ? 'Issued' :
                           txn.transaction_type === 'transfer' ? 'Transferred' : 'Adjusted'}: 
                          {txn.quantity} units • {txn.performed_by}
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          {new Date(txn.performed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : stats?.requisitionStats ? (
                  <>
                    <div className="flex items-start gap-4 py-3 border-b border-border">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <ShoppingCart className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {stats.requisitionStats.approved} Requisitions Approved
                        </p>
                        <p className="text-sm text-text-secondary">
                          Total approved this period
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          Based on current data
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 py-3 border-b border-border">
                      <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {stats.pendingApprovals} Pending Approvals
                        </p>
                        <p className="text-sm text-text-secondary">
                          Requiring attention
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          Action needed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 py-3 border-b border-border">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <Truck className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {stats.poStats.ordered} Purchase Orders In Transit
                        </p>
                        <p className="text-sm text-text-secondary">
                          {stats.poStats.partial} partially received
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          Awaiting delivery
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 py-3 border-b border-border">
                      <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        <Package className="h-5 w-5 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {stats.lowStockItems} Low Stock Alerts
                        </p>
                        <p className="text-sm text-text-secondary">
                          Items need reordering
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          Critical items highlighted
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-text-muted">
                    No recent activity
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Consumed Items Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Top Consumed Items</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/consumption')}
                >
                  Details
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {topConsumedItems && topConsumedItems.length > 0 ? (
                <div className="space-y-3">
                  {topConsumedItems.slice(0, 5).map((item: any, index: number) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {item.item_name}
                          </p>
                          <span className="text-sm text-text-muted">
                            {item.total_consumed}
                          </span>
                        </div>
                        <Progress 
                          value={(item.total_consumed / Math.max(...topConsumedItems.map((i: any) => i.total_consumed))) * 100} 
                          className="h-2" 
                        />
                        <p className="text-xs text-text-muted mt-1">
                          {item.category_name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-text-muted">
                  No consumption data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column - 1/3 width */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/requisitions')}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Create Requisition
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/receive-goods')}
              >
                <Truck className="mr-2 h-4 w-4" />
                Receive Goods
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/stock-transfers')}
              >
                <Package className="mr-2 h-4 w-4" />
                Transfer Stock
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/reports')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View Reports
              </Button>
            </CardContent>
          </Card>

          {/* Category Breakdown - NEW */}
          <Card>
            <CardHeader>
              <CardTitle>Inventory by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {categoryBreakdown && categoryBreakdown.length > 0 ? (
                <DonutChart
                  data={categoryBreakdown}
                  height={200}
                  centerValue={formatCurrency(categoryBreakdown.reduce((sum, c) => sum + c.value, 0))}
                  centerLabel="Total Value"
                  formatValue={(v) => formatCurrency(v)}
                  showPercentage
                />
              ) : (
                <div className="h-48 bg-gradient-to-br from-background-subtle to-background rounded-lg flex items-center justify-center">
                  <p className="text-text-muted text-sm">No category data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vendor Performance - Connected to Real API */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Vendor Performance</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/reports')}
                >
                  Details
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {vendorPerformanceLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg animate-pulse">
                      <div className="h-10 w-10 rounded-full bg-muted" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-24 bg-muted rounded" />
                        <div className="h-2 w-full bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : vendorPerformance && vendorPerformance.length > 0 ? (
                <div className="space-y-4">
                  {vendorPerformance.slice(0, 5).map((vendor, index) => {
                    const score = vendor.on_time_delivery || 0;
                    const bgColor = score >= 90 ? 'bg-green-50' : score >= 70 ? 'bg-blue-50' : 'bg-yellow-50';
                    const textColor = score >= 90 ? 'text-green-600' : score >= 70 ? 'text-blue-600' : 'text-yellow-600';
                    const barColor = score >= 90 ? 'bg-green-500' : score >= 70 ? 'bg-blue-500' : 'bg-yellow-500';
                    
                    return (
                      <div key={vendor.vendor_id} className={cn('flex items-center gap-3 p-3 rounded-lg', bgColor)}>
                        <div className={cn('h-10 w-10 rounded-full flex items-center justify-center', bgColor.replace('50', '100'))}>
                          <TrendingUp className={cn('h-5 w-5', textColor)} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-text-primary truncate">{vendor.vendor_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={score} className={cn('h-2 flex-1 [&>div]:', barColor)} />
                            <span className={cn('text-xs font-medium', textColor)}>{Math.round(score)}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-text-muted text-center pt-2">
                    Based on on-time delivery rate
                  </p>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="h-12 w-12 rounded-full bg-muted mx-auto flex items-center justify-center mb-3">
                    <TrendingUp className="h-6 w-6 text-text-muted" />
                  </div>
                  <p className="text-sm text-text-secondary">No vendor data available</p>
                  <p className="text-xs text-text-muted mt-1">Vendor performance will appear after POs are created</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low Stock Alerts */}
          <Card>
            <CardHeader>
              <CardTitle>Low Stock Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lowStockLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                        <Skeleton className="h-5 w-16" />
                      </div>
                      <Skeleton className="h-2 w-full" />
                    </div>
                  ))
                ) : lowStockItems.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="h-12 w-12 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-3">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <p className="text-sm text-text-secondary">All items are well stocked!</p>
                    <p className="text-xs text-text-muted mt-1">No low stock alerts</p>
                  </div>
                ) : (
                  lowStockItems.slice(0, 5).map((item) => {
                    const minStock = item.min_stock_level || 0;
                    const available = item.available_quantity || 0;
                    const percent = minStock > 0 ? (available / minStock) * 100 : 0;
                    const isCritical = percent < 30;

                    return (
                      <div key={item.item_id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-text-primary truncate max-w-[180px]">
                              {item.item_name}
                            </p>
                            <p className="text-xs text-text-muted">{item.item_code}</p>
                          </div>
                          <Badge variant={isCritical ? 'error' : 'warning'}>
                            {available}/{minStock}
                          </Badge>
                        </div>
                        <div className="h-2 bg-border rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full transition-all duration-300',
                              isCritical ? 'bg-red-500' : 'bg-yellow-500'
                            )}
                            style={{ width: `${Math.min(percent, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
                <Button
                  variant="ghost"
                  className="w-full text-sm mt-2 p-0 h-auto"
                  onClick={() => navigate('/inventory?filter=low_stock')}
                >
                  View all low stock items →
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Expiring Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Expiring Soon</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/expiry')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {expiringItems && expiringItems.length > 0 ? (
                  expiringItems.slice(0, 5).map((item: any, index: number) => {
                    const isCritical = item.days_until_expiry <= 7;
                    return (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {item.item_name}
                          </p>
                          <p className="text-xs text-text-muted">
                            {item.quantity} units • {item.location_name}
                          </p>
                        </div>
                        <Badge variant={isCritical ? 'error' : 'warning'}>
                          {item.days_until_expiry}d
                        </Badge>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-6">
                    <div className="h-12 w-12 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-3">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <p className="text-sm text-text-secondary">No expiring items</p>
                    <p className="text-xs text-text-muted mt-1">All items within safe expiry range</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardHeader>
              <CardTitle>System Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">API Health</span>
                  <Badge variant="success">Operational</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Database</span>
                  <Badge variant="success">Connected</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Last Sync</span>
                  <span className="text-sm text-text-muted">Just now</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Active Hospital</span>
                  <Badge variant="info">{user?.hospital_name || 'Demo'}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
